<!-- Hero Section -->
<section class="relative min-h-[60vh] flex items-center justify-center overflow-hidden gradient-dark pt-24">
  <!-- Background Elements -->
  <div class="absolute inset-0 overflow-hidden">
    <div class="absolute top-20 left-10 w-96 h-96 bg-primary-500/20 rounded-full mix-blend-multiply filter blur-3xl animate-pulse-slow"></div>
    <div class="absolute top-40 right-10 w-96 h-96 bg-accent-500/20 rounded-full mix-blend-multiply filter blur-3xl animate-pulse-slow" style="animation-delay: 2s;"></div>
  </div>

  <div class="container-max relative z-10 text-white text-center">
    <div class="space-y-8 max-w-4xl mx-auto">
      <span class="inline-block px-4 py-2 bg-primary-500/20 border border-primary-400/40 text-primary-200 rounded-full text-sm font-semibold backdrop-blur-sm">
        Our Story
      </span>
      <h1 class="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight drop-shadow-lg">
        Foundations for a
        <br />
        <span class="bg-gradient-to-r from-primary-400 via-accent-400 to-primary-300 bg-clip-text text-transparent">Growing Future</span>
      </h1>
      <p class="text-xl lg:text-2xl text-slate-300 max-w-3xl mx-auto">
        Since 2010, Velora Infra has built trust through excellence, innovation, and quality.
      </p>
    </div>
  </div>
</section>

<!-- Company Story Section -->
<section class="section-padding bg-white">
  <div class="container-max">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <!-- Content -->
      <div class="space-y-8">
        <div>
          <span class="inline-block px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold mb-4">
            Our Journey
          </span>
          <h2 class="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Building Excellence for Over a Decade
          </h2>
        </div>

        <div class="space-y-6 text-lg text-slate-600 leading-relaxed">
          <p>
            Velora Infra was founded on a simple yet powerful vision: to redefine construction excellence through integrity, innovation, and unwavering commitment to quality. What started as a small team of passionate professionals has grown into one of the region's most trusted construction companies.
          </p>
          <p>
            Over 14 years, we've developed more than 2 lakh square feet of premium residential and commercial spaces ranging from luxury complexes to state-of-the-art commercial developments and critical infrastructure. Each project stands as a testament to our dedication to turning dreams into built realities.
          </p>
          <p>
            Today, with over 310 commercial units successfully sold and a track record of 98% client satisfaction, we continue to set new standards in the construction industry. We don't just build structures – we build trust, relationships, and legacies.
          </p>
        </div>

        <!-- Stats -->
        <div class="grid grid-cols-2 gap-6 mt-10">
          <div 
            *ngFor="let stat of stats"
            class="p-6 bg-slate-50 rounded-xl border border-slate-200"
          >
            <p class="text-4xl font-bold text-primary-600 mb-2">{{ stat.value }}</p>
            <p class="text-sm text-slate-600 font-medium">{{ stat.label }}</p>
          </div>
        </div>
      </div>

      <!-- Image -->
      <div class="relative h-96 lg:h-full min-h-96 rounded-2xl overflow-hidden">
        <img 
          src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=800&h=600&fit=crop"
          alt="Construction Excellence"
          class="w-full h-full object-cover"
        >
        <div class="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
      </div>
    </div>
  </div>
</section>

<!-- Vision & Mission Section -->
<section class="section-padding bg-gradient-to-b from-slate-50 to-white">
  <div class="container-max">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
      <!-- Vision -->
      <div class="p-12 bg-gradient-to-br from-primary-50 to-accent-50 rounded-3xl border border-primary-200">
        <div class="w-16 h-16 bg-gradient-to-br from-primary-600 to-accent-500 rounded-xl flex items-center justify-center mb-6">
          <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <h3 class="text-3xl font-bold text-slate-900 mb-4">Our Vision</h3>
        <p class="text-lg text-slate-700 leading-relaxed">
          To be recognized as the most trusted and innovative construction partner in the region, known for delivering exceptional projects that enhance communities and create lasting value for all stakeholders.
        </p>
      </div>

      <!-- Mission -->
      <div class="p-12 bg-gradient-to-br from-accent-50 to-primary-50 rounded-3xl border border-accent-200">
        <div class="w-16 h-16 bg-gradient-to-br from-primary-600 to-accent-500 rounded-xl flex items-center justify-center mb-6">
          <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
          </svg>
        </div>
        <h3 class="text-3xl font-bold text-slate-900 mb-4">Our Mission</h3>
        <p class="text-lg text-slate-700 leading-relaxed">
          To deliver world-class construction solutions that exceed expectations through meticulous planning, innovative execution, and transparent communication while upholding the highest standards of safety, sustainability, and quality.
        </p>
      </div>
    </div>

    <!-- Values Section -->
    <div class="mt-24">
      <div class="text-center mb-16">
        <h2 class="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
          Our Core Values
        </h2>
        <p class="text-xl text-slate-600 max-w-2xl mx-auto">
          These principles guide every decision and action we take
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        <div 
          *ngFor="let value of values"
          class="p-8 bg-white border border-slate-200 rounded-2xl hover:shadow-lg transition-all duration-300"
        >
          <h4 class="text-2xl font-bold text-slate-900 mb-3">{{ value.title }}</h4>
          <p class="text-slate-600 leading-relaxed">{{ value.description }}</p>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Leadership Section -->
<!-- <section class="section-padding bg-white">
  <div class="container-max">
    <div class="text-center mb-16">
      <span class="inline-block px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold mb-4">
        Leadership
      </span>
      <h2 class="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
        Meet Our Leadership Team
      </h2>
      <p class="text-xl text-slate-600 max-w-2xl mx-auto">
        Experienced professionals driving innovation and excellence across all operations
      </p>
    </div>
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
      <div 
        *ngFor="let leader of leadership"
        class="group text-center"
      >
        <div class="relative h-64 mb-6 rounded-2xl overflow-hidden">
          <img 
            [src]="leader.image"
            [alt]="leader.name"
            class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
          >
          <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
            <div class="text-white">
              <div class="flex flex-wrap gap-2 justify-center">
                <span 
                  *ngFor="let skill of leader.expertise"
                  class="text-xs px-2 py-1 bg-white/20 backdrop-blur rounded-full"
                >
                  {{ skill }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <h3 class="text-2xl font-bold text-slate-900 mb-2">{{ leader.name }}</h3>
        <p class="text-primary-600 font-semibold text-lg mb-4">{{ leader.position }}</p>
        <p class="text-slate-600 leading-relaxed">{{ leader.description }}</p>
      </div>
    </div>
  </div>
</section> -->

<!-- CTA Section -->
<section class="section-padding gradient-dark text-white relative overflow-hidden">
  <div class="absolute inset-0 opacity-10">
    <div class="absolute top-0 right-0 w-96 h-96 bg-primary-500 rounded-full mix-blend-multiply filter blur-3xl"></div>
    <div class="absolute bottom-0 left-0 w-96 h-96 bg-accent-500 rounded-full mix-blend-multiply filter blur-3xl"></div>
  </div>

  <div class="container-max relative z-10 text-center">
    <h2 class="text-4xl lg:text-5xl font-bold mb-6">
      Ready to Build With Us?
    </h2>
    <p class="text-xl text-slate-300 max-w-2xl mx-auto mb-10">
      Join the growing list of satisfied clients who have trusted Velora Infra with their most important projects.
    </p>
    <div class="flex flex-col sm:flex-row gap-4 justify-center">
      <a 
        routerLink="/contact"
        class="px-8 py-4 bg-gradient-to-r from-primary-600 to-accent-500 text-white font-bold rounded-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
      >
        Contact Us Today
      </a>
      <a 
        routerLink="/"
        class="px-8 py-4 border-2 border-primary-300 text-primary-300 font-bold rounded-lg hover:bg-white/10 transition-all duration-300"
      >
        View Our Work
      </a>
    </div>
  </div>
</section>
